/* @flow */

import * as React from 'react';

import type { AllTracks, AudioTrack, ShakaOfflineProgressUpdate, ShakaTrack, TextTrack, VideoTrack } from './shakaTypes';

import { logInfo } from '../../../helpers/debug/debug';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import ButtonFX from '../../buttons/ButtonFX';
import { HeightKind, WidthKind } from '../../buttons/types';
import ShakaStorage from './shakaStorage';

const cloneAudioTracks: (tracks: Array<AudioTrack>) => Array<AudioTrack> = (tracks) =>
  tracks.map((track) => {
    return {
      ...track,
      roles: [...track.roles],
    };
  });

const cloneTextTracks: (tracks: Array<TextTrack>) => Array<TextTrack> = (tracks) =>
  tracks.map((track) => {
    return {
      ...track,
      roles: [...track.roles],
    };
  });

const cloneVideoTracks: (tracks: Array<VideoTrack>) => Array<VideoTrack> = (tracks) =>
  tracks.map((track) => {
    return {
      ...track,
      roles: [...track.roles],
    };
  });

// TODO: use cache
const cloneAllTracks: (tracks: AllTracks) => AllTracks = (tracks) => {
  const { audio, text, video } = tracks;

  return {
    audio: cloneAudioTracks(audio),
    text: cloneTextTracks(text),
    video: cloneVideoTracks(video),
  };
};

const handleCancelOnClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>, id: string) => void = (event, id) => {
  if (id) {
    ShakaStorage.cancelDownload(id);
  }
};

// Sending progress of 1 will immediately close the toast, whatever the value of autoClose
const ALMOST_ONE = 0.99;
const generateUpdateDownloadProgress: (toastId: string, title: string) => ShakaOfflineProgressUpdate =
  (toastId, title) =>
  (content: any, progress: number): void => {
    if (progress === -1) {
      // Error
      Messenger.emit(MessengerEvents.NOTIFY_ERROR, `Erreur lors du téléchargement de &#xAB;&nbsp;${title}&nbsp;&#xBB;`, { progress, toastId });
      Messenger.emit(MessengerEvents.NOTIFY_CLOSE, toastId);
    } else if (progress < 1) {
      // Downloading
      Messenger.emit(
        MessengerEvents.NOTIFY_INFO,
        <div className='toastContent'>
          <div>Téléchargement de &#xAB;&nbsp;{title}&nbsp;&#xBB;...</div>
          <ButtonFX data={toastId} heightKind={HeightKind.Small} onClick={handleCancelOnClick} widthKind={WidthKind.Stretched}>
            Annuler
          </ButtonFX>
        </div>,
        { progress, toastId },
        `Téléchargement de ${title} en cours`,
      );
    } else {
      // Finished
      Messenger.emit(MessengerEvents.NOTIFY_SUCCESS, `&#xAB;&nbsp;${title}&nbsp;&#xBB; a été téléchargé`, { progress: ALMOST_ONE, toastId });
      Messenger.emit(MessengerEvents.NOTIFY_CLOSE, toastId);
      Messenger.emit(MessengerEvents.DOWNLOAD_SUCCESSFUL);
    }
  };

const selectDownloadTracks: (tracks: Array<ShakaTrack>) => Array<ShakaTrack> = (tracks) => {
  let index = -1;
  let maxBandwidth = -1;
  for (let i = 0; i < tracks.length; ++i) {
    const {
      [i]: { bandwidth, type },
    } = tracks;
    if (type === 'variant' && bandwidth > maxBandwidth) {
      index = i;
      maxBandwidth = bandwidth;
    }
  }

  if (index > -1) {
    // Track with the highest bandwidth found
    return [tracks[index]];
  }

  // First variant track selected
  const firstTrack = tracks.find(({ type }) => type === 'variant');
  if (firstTrack) {
    return [firstTrack];
  }

  // No track
  return [];
};

const Verimatrix = Object.freeze({ Case2LastIndex: 6, CharCodeRadix: 16, HexCharCodeLength: 2, UuidLength: 32 });

const getVerimatrixContentId: (skdOriginalValue: string) => string = (skdOriginalValue) => {
  let contentId: string | null = null;

  // [Case 1] request id: skd://e670fd8208733965b03cfd818d21e3fc → content key id: e670fd8208733965b03cfd818d21e3fc
  const ma1 = skdOriginalValue.match(/\/([a-z\d]+)$/iu);
  if (ma1) {
    [, contentId] = ma1;
  } else {
    // [Case 2] request id: skd://fps.ezdrm.com/;d9fb2bc6-6e03-4206-abfe-665405e083cc → content key id: d9fb2bc66e034206abfe665405e083cc
    const ma2 = skdOriginalValue.match(/;([a-z\d]{8})-([a-z\d]{4})-([a-z\d]{4})-([a-z\d]{4})-([a-z\d]{12})$/iu);
    if (ma2) {
      contentId = ma2.slice(1, Verimatrix.Case2LastIndex).join('');
    }
  }

  const base64Re = /^(?:[A-Z0-9+/]{4})*(?:[A-Z0-9+/]{2}==|[A-Z0-9+/]{3}=|[A-Z0-9+/]{4})$/iu;
  if (contentId !== null && base64Re.test(contentId)) {
    logInfo(`contentId "${contentId}" is valid base64`);

    const result = atob(contentId)
      .split('')
      .map((c) => c.charCodeAt(0).toString(Verimatrix.CharCodeRadix).padStart(Verimatrix.HexCharCodeLength, '0'))
      .join('');

    if (result.length === Verimatrix.UuidLength) {
      contentId = result;
    }
  } else {
    logInfo(`contentId "${contentId ?? 'null'}" is not valid base64`);
  }

  // If contentId is null, return original SKD
  return contentId || skdOriginalValue;
};

export { cloneAllTracks, getVerimatrixContentId, selectDownloadTracks, generateUpdateDownloadProgress };
